import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMe, useUpdateMe } from '@/api/queries/user';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { OnboardingLayout } from '../Layout';
import { paths } from '@/routes/paths';
import { useUIStore } from '@/store/ui';
import { PhotoUploader } from '@/components/domain/PhotoUploader';
import type { Gender } from '@/api/types';

export function OnboardingProfilePage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const me = useMe();
  const update = useUpdateMe();
  const pushToast = useUIStore((s) => s.pushToast);

  const [given, setGiven] = useState(me.data?.given_name ?? '');
  const [surname, setSurname] = useState(me.data?.surname ?? '');
  const [dob, setDob] = useState(me.data?.date_of_birth ?? '');
  const [gender, setGender] = useState<Gender | ''>((me.data?.gender as Gender) ?? '');
  // Object-key returned by /uploads/presign for the avatar. Null = keep
  // whatever was already on the profile.
  const [avatarKey, setAvatarKey] = useState<string | null>(null);

  const canSubmit = given && surname && dob && gender;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !gender) return;
    try {
      await update.mutateAsync({
        given_name: given,
        surname,
        date_of_birth: dob,
        gender,
        ...(avatarKey ? { avatar_object_key: avatarKey } : {}),
      });
      navigate(paths.onboardingPassport);
    } catch {
      pushToast({ body: t('errors.generic'), variant: 'error' });
    }
  };

  return (
    <OnboardingLayout back title={t('onboarding.profile.title')}>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <PhotoUploader
          purpose="avatar"
          existingUrl={me.data?.avatar_url ?? null}
          onUploaded={(key) => setAvatarKey(key)}
          label={t('onboarding.profile.avatar')}
          shape="circle"
        />
        <Input
          label={t('onboarding.profile.given_name')}
          value={given}
          onChange={(e) => setGiven(e.target.value)}
          autoComplete="given-name"
          required
        />
        <Input
          label={t('onboarding.profile.surname')}
          value={surname}
          onChange={(e) => setSurname(e.target.value)}
          autoComplete="family-name"
          required
        />
        <Input
          label={t('onboarding.profile.date_of_birth')}
          type="date"
          value={dob}
          onChange={(e) => setDob(e.target.value)}
          required
        />
        <Select
          label={t('onboarding.profile.gender.label')}
          placeholder="—"
          value={gender}
          onChange={(e) => setGender(e.target.value as Gender)}
          options={[
            { value: 'male', label: t('onboarding.profile.gender.male') },
            { value: 'female', label: t('onboarding.profile.gender.female') },
            { value: 'other', label: t('onboarding.profile.gender.other') },
          ]}
        />
        <Button
          type="submit"
          block
          size="lg"
          variant="inverted"
          disabled={!canSubmit}
          loading={update.isPending}
        >
          {t('common.continue')}
        </Button>
      </form>
    </OnboardingLayout>
  );
}
